import { log } from '@/logger/logger.ts';
import { getOidcAccessToken } from '@/services/auth/firebase/oidc-providers/figma-oidc-provider';
import { getAxiosInstance } from '@/services/axiosConfig';

const logger = log.getLogger('figma-token-service');

interface CachedToken {
  accessToken: string;
  expiresAt: string;
}

let cachedFigmaToken: CachedToken | null = null;
let pendingTokenFetch: Promise<string | null> | null = null;

async function fetchFigmaTokenFromServer(): Promise<string | null> {
  try {
    const { data } = await getAxiosInstance().get<{
      connected: boolean;
      accessToken?: string;
      expiresAt?: string;
    }>('/v1/auth/figma/token');

    if (data.connected && data.accessToken && data.expiresAt) {
      cachedFigmaToken = { accessToken: data.accessToken, expiresAt: data.expiresAt };
      return data.accessToken;
    }
    return null;
  } catch (error) {
    logger.warn('Failed to fetch Figma token from server:', error);
    return null;
  }
}

function getValidCachedToken(): string | null {
  if (!cachedFigmaToken) return null;

  const expiresAtMs = new Date(cachedFigmaToken.expiresAt).getTime();
  if (isNaN(expiresAtMs)) return null;

  // Consider expired 1 minute before actual expiry
  if (expiresAtMs - Date.now() <= 60_000) return null;

  return cachedFigmaToken.accessToken;
}

export function clearCachedFigmaToken(): void {
  cachedFigmaToken = null;
  pendingTokenFetch = null;
}

export async function getFigmaToken(): Promise<string | null> {
  const cached = getValidCachedToken();
  if (cached) return cached;

  // Deduplicate concurrent fetches
  if (!pendingTokenFetch) {
    pendingTokenFetch = fetchFigmaTokenFromServer();
  }

  try {
    const serverToken = await pendingTokenFetch;
    if (serverToken) return serverToken;
  } finally {
    pendingTokenFetch = null;
  }

  // Fallback to localStorage for migration period
  return getOidcAccessToken();
}
